// src/hooks/useAuth.ts
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { trpc } from "../providers/trpc";
import { toast } from "sonner";

interface User {
  id: number;
  name: string | null;
  email: string | null;
  role: string;
  avatar?: string;
}

const STORAGE_KEY = "pdv_user_local";

function getStoredUser(): User | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && parsed.id) {
        return parsed;
      }
    }
  } catch { /* intentionally empty */ }
  return null;
}

function setStoredUser(user: User | null) {
  if (user) localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  else localStorage.removeItem(STORAGE_KEY);
}

export function useAuth(options?: { redirectOnUnauthenticated?: boolean; redirectPath?: string }) {
  const { redirectOnUnauthenticated = false, redirectPath = "/login" } = options ?? {};
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const utils = trpc.useUtils();

  // Carrega usuário do localStorage ao iniciar
  useEffect(() => {
    const stored = getStoredUser();
    setUser(stored);
    setIsLoading(false);
  }, []);

  // Redireciona se não estiver autenticado
  useEffect(() => {
    if (redirectOnUnauthenticated && !isLoading && !user) {
      navigate(redirectPath);
    }
  }, [redirectOnUnauthenticated, isLoading, user, navigate, redirectPath]);

  // ─── MUTATION DE LOGIN ───
  const loginMutation = trpc.auth.login.useMutation({
    onSuccess: (data) => {
      setUser(data.user);
      setStoredUser(data.user);
      toast.success(`Bem-vindo, ${data.user.name}!`);
      // Redireciona para a página inicial
      window.location.href = "/";
    },
    onError: (error) => {
      console.error("Login failed:", error.message);
      toast.error("Erro ao fazer login: " + error.message);
    },
  });

  // ─── MUTATION DE LOGOUT ───
  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      setUser(null);
      setStoredUser(null);
      utils.invalidate();
      toast.success("Logout realizado com sucesso!");
      window.location.href = "/login";
    },
    onError: () => {
      // Mesmo com erro, limpa localmente
      setUser(null);
      setStoredUser(null);
      utils.invalidate();
      window.location.href = "/login";
    },
  });

  // ─── FUNÇÃO DE LOGIN (corrigida) ───
  const login = useCallback(async (usernameOrEmail: string, password: string) => {
    if (!usernameOrEmail || !password) {
      toast.error("Preencha usuário e senha");
      return;
    }
    // Limpa usuário anterior antes de tentar login
    setUser(null);
    // Retorna a promise para o componente poder usar await
    return new Promise((resolve, reject) => {
      loginMutation.mutate(
        { email: usernameOrEmail, password },
        {
          onSuccess: (data) => {
            setUser(data.user);
            setStoredUser(data.user);
            toast.success(`Bem-vindo, ${data.user.name}!`);
            window.location.href = "/";
            resolve(data);
          },
          onError: (error) => {
            reject(error);
          },
        }
      );
    });
  }, [loginMutation]);

  // ─── FUNÇÃO DE LOGOUT ───
  const logout = useCallback(() => {
    setUser(null);
    setStoredUser(null);
    logoutMutation.mutate();
  }, [logoutMutation]);

  // ─── VERIFICA SE O USUÁRIO TEM PERMISSÃO ───
  const hasRole = useCallback((role: string | string[]) => {
    if (!user) return false;
    if (Array.isArray(role)) {
      return role.includes(user.role);
    }
    return user.role === role;
  }, [user]);

  return useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading: isLoading || loginMutation.isPending || logoutMutation.isPending,
      logout,
      login,
      hasRole,
      refresh: () => {
        const stored = getStoredUser();
        setUser(stored);
      },
    }),
    [user, isLoading, logout, login, hasRole, loginMutation.isPending, logoutMutation.isPending]
  );
}